import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pageTitle } from "@/brand";
import SitewrapLogo from "@/components/SitewrapLogo";
import "@/styles/sitewrap-logo.css";
import { dashFetch, getActiveSite, setActiveSite } from "@/api/client";
import DashLoader, { DashUserSkeleton } from "@/components/DashLoader";
import { Card, Toolbar } from "@/components/Card";
import AddSiteWizard from "@/components/AddSiteWizard";
import DeleteSiteModal from "@/components/DeleteSiteModal";
import UserModal from "@/account/UserModal";
import ProfilePanel from "@/account/ProfilePanel";
import BillingPanel from "@/account/BillingPanel";
import GlobalSettingsPanel from "@/account/GlobalSettingsPanel";
import SettingsSkeletonsPanel from "@/account/SettingsSkeletonsPanel";
import PlansPanel from "@/account/PlansPanel";
import { fetchAddSiteWizardResumeStep } from "@/utils/addSiteWizardResume";
import { friendlyProvisionError } from "@/utils/provisionErrors";
import type { AccountTab, AccountUser, BillingRates, TenantRow } from "@/types/account";
import {
  ACCOUNT_NAV_SECTIONS,
  ACCOUNT_PANEL_META,
  resolveAccountHash,
  sectionForAccountTab,
  type AccountNavSection
} from "@/nav/navConfig";
import { NavTabIcon } from "@/nav/NavTabIcon";
import "@/styles/account-console.css";

const BILLING_IDS = [
  "diskStorage", "bandwidth", "crawling", "optimization", "leads",
  "requests", "forms", "recordings", "deployments", "liveSessions", "aiAssistant"
] as const;

function tabFromHash(): AccountTab {
  return resolveAccountHash(location.hash || "");
}

function userInitials(user: AccountUser): string {
  const name = String(user.displayName || "").trim();
  const email = String(user.email || "").trim();
  const src = name || email;
  if (!src) return "U";
  const left = src.includes("@") ? src.split("@")[0] || src : src;
  const parts = left.split(/[._\-\\s]+/).filter(Boolean);
  const a = (parts[0] || left || "U").slice(0, 1).toUpperCase();
  const b = (parts[1] || "").slice(0, 1).toUpperCase();
  return (a + b).slice(0, 2);
}

function dashboardHref(domain: string): string {
  return `/dashboard?site=${encodeURIComponent(domain)}`;
}

function SiteFavicon({ domain }: { domain: string }) {
  const [hasImg, setHasImg] = useState(false);
  const letter = (domain || "?").charAt(0).toUpperCase();
  const src = `/dashboard/api/site-favicon?domain=${encodeURIComponent(domain.toLowerCase())}`;
  return (
    <span className={"siteIcon" + (hasImg ? " hasImg" : "")}>
      {!hasImg ? letter : null}
      <img
        className="siteIconFavicon"
        src={src}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onLoad={(e) => {
          const img = e.currentTarget;
          if ((img.naturalWidth || 0) >= 4 && (img.naturalHeight || 0) >= 4) setHasImg(true);
        }}
        onError={() => setHasImg(false)}
      />
    </span>
  );
}

export default function AccountShell() {
  const [activeTab, setActiveTab] = useState<AccountTab>(tabFromHash);
  const [user, setUser] = useState<AccountUser>({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [allTenants, setAllTenants] = useState<TenantRow[]>([]);
  const [creditsByDomain, setCreditsByDomain] = useState<Record<string, number>>({});

  const [users, setUsers] = useState<AccountUser[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [userModal, setUserModal] = useState<{ open: boolean; edit?: AccountUser }>({ open: false });
  const [userMsg, setUserMsg] = useState({ err: "", ok: "" });

  const [billingRates, setBillingRates] = useState<BillingRates | null>(null);
  const [billingDraft, setBillingDraft] = useState<Record<string, number>>({});
  const [billingMsg, setBillingMsg] = useState("");

  const [addSiteOpen, setAddSiteOpen] = useState(false);
  const [addSiteResumeDomain, setAddSiteResumeDomain] = useState("");
  const [addSiteResumeStep, setAddSiteResumeStep] = useState(0);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; domain: string }>({ open: false, domain: "" });
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const userWrapRef = useRef<HTMLDivElement>(null);

  const setTab = useCallback((tab: AccountTab) => {
    setActiveTab(tab);
    const h = `#${tab}`;
    if (location.hash !== h) history.replaceState(null, "", h);
  }, []);

  useEffect(() => {
    document.title = pageTitle("Account");
  }, []);

  useEffect(() => {
    const onHash = () => setActiveTab(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const r = await dashFetch<{ user?: AccountUser }>("/dashboard/api/me", { cache: "no-store" });
      if (r?.user) setUser(r.user);
    } catch {
      setUser({});
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const r = await dashFetch<{ ok?: boolean; users?: AccountUser[] }>("/dashboard/api/account/users", { cache: "no-store" });
      if (r?.ok && Array.isArray(r.users)) setUsers(r.users);
    } catch {
      setUsers([]);
    }
  }, []);

  const loadBillingRates = useCallback(async () => {
    try {
      const r = await dashFetch<{ ok?: boolean; rates?: BillingRates }>("/dashboard/api/account/billing-rates", { cache: "no-store" });
      if (!r?.ok || !r.rates) throw new Error("load failed");
      setBillingRates(r.rates);
      const draft: Record<string, number> = {};
      for (const id of BILLING_IDS) {
        draft[id] = Number(r.rates.categories?.[id]?.creditsPerUnit ?? 0);
      }
      setBillingDraft(draft);
    } catch (e) {
      setBillingMsg(`Failed to load: ${String(e instanceof Error ? e.message : e)}`);
    }
  }, []);

  const loadContext = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await dashFetch<{
        ok?: boolean;
        isAdministrator?: boolean;
        tenants?: TenantRow[];
        allTenants?: TenantRow[];
      }>("/dashboard/api/account/context", { cache: "no-store" });
      if (!r?.ok) throw new Error("context failed");
      setIsAdmin(Boolean(r.isAdministrator));
      setTenants(Array.isArray(r.tenants) ? r.tenants : []);
      setAllTenants(Array.isArray(r.allTenants) ? r.allTenants : (r.tenants || []));
      if (r.isAdministrator) {
        await loadUsers();
        await loadBillingRates();
      }
      try {
        const br = await dashFetch<{ ok?: boolean; sites?: { domain?: string; creditsTotal?: number }[] }>(
          "/dashboard/api/account/billing-summary",
          { cache: "no-store" }
        );
        if (br?.ok && Array.isArray(br.sites)) {
          const map: Record<string, number> = {};
          for (const s of br.sites) {
            const k = String(s.domain || "").toLowerCase();
            if (k) map[k] = Number(s.creditsTotal || 0);
          }
          setCreditsByDomain(map);
        }
      } catch { /* optional */ }
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setLoading(false);
    }
  }, [loadBillingRates, loadUsers]);

  useEffect(() => {
    void loadMe();
    void loadContext();
  }, [loadContext, loadMe]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (userWrapRef.current && !userWrapRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleLogout = async () => {
    try {
      await dashFetch("/dashboard/api/logout", { method: "POST" });
    } catch { /* ignore */ }
    location.href = "/";
  };

  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return String(u.email || "").toLowerCase().includes(q) || String(u.role || "").toLowerCase().includes(q);
  });

  const saveBillingRates = async () => {
    setBillingMsg("Saving…");
    try {
      const cats: Record<string, unknown> = {};
      for (const id of BILLING_IDS) {
        const prev = billingRates?.categories?.[id] || {};
        cats[id] = { ...prev, creditsPerUnit: Number(billingDraft[id] || 0) };
      }
      const r = await dashFetch<{ ok?: boolean; rates?: BillingRates; error?: string }>(
        "/dashboard/api/account/billing-rates",
        { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ categories: cats }) }
      );
      if (!r?.ok) throw new Error(String(r.error || "save failed"));
      if (r.rates) setBillingRates(r.rates);
      setBillingMsg("Saved. New rates apply to all sites immediately.");
    } catch (e) {
      setBillingMsg(`Save failed: ${String(e instanceof Error ? e.message : e)}`);
    }
  };

  const saveUser = async (payload: { email: string; role: string; password?: string; allowedSites: string[] }, isEdit: boolean) => {
    const r = await dashFetch<{ ok?: boolean; error?: string }>("/dashboard/api/account/users", {
      method: isEdit ? "PATCH" : "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (!r?.ok) throw new Error(String(r.error || "save failed"));
    setUserMsg({ ok: isEdit ? "User updated." : "User created.", err: "" });
    await loadUsers();
    setUserModal({ open: false });
  };

  const deleteUser = async (email: string) => {
    if (!confirm(`Delete user ${email}?`)) return;
    const r = await dashFetch<{ ok?: boolean; error?: string }>("/dashboard/api/account/users", {
      method: "DELETE",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email })
    });
    if (!r?.ok) throw new Error(String(r.error || "delete failed"));
    setUserMsg({ ok: "User deleted.", err: "" });
    await loadUsers();
  };

  const canDeleteSite = (domain: string): boolean => {
    const d = String(domain || "").trim().toLowerCase();
    if (!d) return false;
    if (isAdmin) return true;
    const allow = new Set((user.allowedSites || []).map((x) => String(x || "").trim().toLowerCase()));
    return allow.has(d);
  };

  const openSetupWizard = useCallback(async (siteDomain: string) => {
    const d = String(siteDomain || "").trim();
    if (!d) return;
    setAddSiteResumeDomain(d);
    setAddSiteResumeStep(1);
    setAddSiteOpen(true);
    const step = await fetchAddSiteWizardResumeStep(d);
    setAddSiteResumeDomain(d);
    setAddSiteResumeStep(step);
    setActiveSite(d);
  }, []);

  const openDeleteSite = (domain: string) => {
    setDeleteConfirm("");
    setDeleteError("");
    setDeleteModal({ open: true, domain });
  };

  const closeDeleteSite = () => {
    if (deleteBusy) return;
    setDeleteModal({ open: false, domain: "" });
    setDeleteConfirm("");
    setDeleteError("");
  };

  const submitDeleteSite = async () => {
    const domain = deleteModal.domain;
    if (!domain) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const r = await dashFetch<{ ok?: boolean; error?: string; message?: string }>(
        "/dashboard/api/tenants/delete",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ domain, confirm: deleteConfirm.trim() })
        }
      );
      if (!r?.ok) {
        setDeleteError(friendlyProvisionError(r));
        return;
      }
      closeDeleteSite();
      await loadContext();
      if (getActiveSite() === domain) {
        const next = tenants.find((t) => String(t.domain || "").toLowerCase() !== domain.toLowerCase());
        if (next?.domain) location.href = dashboardHref(String(next.domain));
        else location.href = "/dashboard/account";
      }
    } catch (e) {
      setDeleteError(friendlyProvisionError(e instanceof Error ? e.message : e));
    } finally {
      setDeleteBusy(false);
    }
  };

  const dashBackHref = tenants[0]?.domain ? dashboardHref(String(tenants[0].domain)) : "/dashboard";
  const visibleSections = useMemo(
    () => ACCOUNT_NAV_SECTIONS.filter((s) => !s.adminOnly || isAdmin),
    [isAdmin]
  );
  const activeSection = useMemo(() => sectionForAccountTab(activeTab), [activeTab]);
  const secondaryPanels = activeSection.panels;
  const showSecondary = secondaryPanels.length > 1;

  const openAccountSection = useCallback((section: AccountNavSection) => {
    const preferred = section.panels.includes(activeTab) ? activeTab : section.panels[0];
    setTab(preferred);
  }, [activeTab, setTab]);

  return (
    <div className="accountWrap">
      <div className="topbar">
        <div className="leftSide">
          <a className="dashHomeBtn" href={dashBackHref} aria-label="Back to dashboard" title="Dashboard">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M3 10.5 12 3l9 7.5" />
              <path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10" />
            </svg>
          </a>
          <div className="accountTitleRow">
            <SitewrapLogo size={28} variant="gradient" className="accountBrandMark" />
            <span className="accountSubtitle">
              Account · {isAdmin ? "Administrator · manage sites, team & platform" : "Choose a site to open the dashboard"}
            </span>
          </div>
        </div>
        <div className="rightSide">
          <div className="userWrap" ref={userWrapRef}>
            <button
              type="button"
              className="userBtn"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              onClick={(e) => { e.stopPropagation(); setUserMenuOpen((v) => !v); }}
            >
              {user.email ? (
                <>
                  <span className="avatar">{userInitials(user)}</span>
                  <span className="userMeta">
                    <span className="userEmail">{user.displayName || user.email}</span>
                    <span className="userRole">{user.displayName ? user.email : (user.role || "—")}</span>
                  </span>
                </>
              ) : (
                <DashUserSkeleton />
              )}
            </button>
            <div className={"userMenu" + (userMenuOpen ? " on" : "")} role="menu" aria-label="User menu">
              <div className="userMenuHeader">
                <div className="mono muted">Signed in</div>
                <div className="mono">{user.email || "—"}</div>
              </div>
              {tenants[0]?.domain ? (
                <a
                  className="userMenuItem"
                  href={dashboardHref(String(tenants[0].domain))}
                  role="menuitem"
                  onClick={() => setUserMenuOpen(false)}
                >
                  Open dashboard
                </a>
              ) : null}
              <button
                type="button"
                className="userMenuItem"
                role="menuitem"
                onClick={() => { setUserMenuOpen(false); setTab("profile"); }}
              >
                Profile
              </button>
              <button
                type="button"
                className="userMenuItem"
                role="menuitem"
                onClick={() => { setUserMenuOpen(false); setTab("billing"); }}
              >
                Billing
              </button>
              <button
                type="button"
                className="userMenuItem danger"
                role="menuitem"
                onClick={() => void handleLogout()}
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>

      <nav className="navPrimary" aria-label="Account">
        <div className="tabs tabsPrimary" role="tablist" aria-label="Account sections">
          {visibleSections.map((section) => {
            const isActive = activeSection.id === section.id;
            return (
              <button
                key={section.id}
                type="button"
                className={"tab tabPrimary" + (isActive ? " active" : "")}
                role="tab"
                aria-selected={isActive}
                title={section.purpose}
                onClick={() => openAccountSection(section)}
              >
                <span className="tabIcon" aria-hidden="true">
                  <NavTabIcon id={section.id} />
                </span>
                <span className="tabLabel">{section.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {showSecondary ? (
        <div className="navSecondary" aria-label={`${activeSection.label} sections`}>
          <div className="tabs tabsSecondary" role="tablist">
            {secondaryPanels.map((panelId) => {
              const meta = ACCOUNT_PANEL_META[panelId];
              const isActive = activeTab === panelId;
              return (
                <button
                  key={panelId}
                  type="button"
                  className={"tab tabSecondary" + (isActive ? " active" : "")}
                  role="tab"
                  aria-selected={isActive}
                  title={meta.description}
                  onClick={() => setTab(panelId)}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
          <p className="navSectionHint muted">{activeSection.purpose}</p>
        </div>
      ) : null}

      {loading ? <DashLoader size="compact" label="Loading account" /> : null}
      {error ? <p className="err" style={{ marginTop: 12 }}>Failed to load: {error}</p> : null}

      {activeTab === "sites" ? (
        <div className="tabPanel active">
          <Card title="Your sites">
            {tenants.length ? (
              <Toolbar>
                <p className="muted" style={{ margin: 0, flex: 1 }}>
                  Open a site dashboard or add another domain.
                </p>
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => {
                    setAddSiteResumeDomain("");
                    setAddSiteResumeStep(0);
                    setAddSiteOpen(true);
                  }}
                >
                  Add website
                </button>
              </Toolbar>
            ) : null}
            {!tenants.length ? (
              <div className="accountSitesEmpty">
                <p className="accountSitesEmptyTitle">You don&apos;t have any sites yet.</p>
                <button
                  type="button"
                  className="accountSitesEmptyBtn"
                  onClick={() => {
                    setAddSiteResumeDomain("");
                    setAddSiteResumeStep(0);
                    setAddSiteOpen(true);
                  }}
                >
                  Add your first website to start using Sitewrap.
                </button>
              </div>
            ) : (
              <div className="siteGrid">
                {tenants.map((t) => {
                  const d = String(t.domain || "");
                  const cred = creditsByDomain[d.toLowerCase()];
                  const pending = String(t.status || "active") === "pending";
                  const dashPreview = Boolean(t.dashboardPreview);
                  return (
                    <div key={d} className="siteCard">
                      <div className="siteRow">
                        <SiteFavicon domain={d} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="d">
                            {d}
                            {pending ? (
                              <span className="badge" style={{ marginLeft: 8 }}>
                                {dashPreview ? "DNS pending" : "Pending"}
                              </span>
                            ) : null}
                          </div>
                          <div className="o">
                            {pending
                              ? dashPreview
                                ? "Site dashboard available · finish DNS to go live"
                                : "Awaiting DNS verification"
                              : t.origin || ""}
                          </div>
                          {typeof cred === "number" ? (
                            <div className="o mono siteCredits">{Math.round(cred * 100) / 100} credits · this month</div>
                          ) : null}
                          <div className="o mono siteCredits">{t.planName || t.planId || "Beta tester"} plan</div>
                        </div>
                      </div>
                      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {pending ? (
                          <>
                            {dashPreview ? (
                              <a className="btn" href={dashboardHref(d)}>Open dashboard</a>
                            ) : null}
                            <button type="button" className="btn primary" onClick={() => void openSetupWizard(d)}>
                              Continue setup
                            </button>
                          </>
                        ) : (
                          <a className="btn primary" href={dashboardHref(d)}>Open dashboard</a>
                        )}
                        {canDeleteSite(d) ? (
                          <button type="button" className="btn danger" onClick={() => openDeleteSite(d)}>
                            Delete site
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      ) : null}

      {activeTab === "profile" ? (
        <div className="tabPanel active">
          <ProfilePanel user={user} onUpdated={(next) => setUser((prev) => ({ ...prev, ...next }))} />
        </div>
      ) : null}

      {activeTab === "billing" ? (
        <div className="tabPanel active">
          <BillingPanel sites={tenants} isAdmin={isAdmin} onPlanChanged={loadContext} />
        </div>
      ) : null}

      {activeTab === "users" ? (
        <div className="tabPanel active">
          {isAdmin ? (
            <Card title="Team">
              <p className="muted">Invite operators and control which sites they can open.</p>
              <Toolbar>
                <input className="search" placeholder="Search users…" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
                <button type="button" className="btn primary" onClick={() => setUserModal({ open: true, edit: undefined })}>Add user</button>
              </Toolbar>
              {userMsg.err ? <p className="err">{userMsg.err}</p> : null}
              {userMsg.ok ? <p className="ok">{userMsg.ok}</p> : null}
              <div className="tableWrap" style={{ marginTop: 12 }}>
                <table className="table mono">
                  <thead>
                    <tr><th>User</th><th>Role</th><th>Sites</th><th style={{ width: 130 }}></th></tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.email}>
                        <td>{u.email}</td>
                        <td>{u.role || "user"}</td>
                        <td>{u.isAdministrator ? "All" : (u.allowedSites || []).join(", ") || "—"}</td>
                        <td>
                          <button type="button" className="btn" onClick={() => setUserModal({ open: true, edit: u })}>Edit</button>
                          {" "}
                          <button type="button" className="btn danger" onClick={() => void deleteUser(String(u.email || "")).catch((e) => setUserMsg({ err: String(e.message), ok: "" }))}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card title="Team"><p className="muted">Only administrators can manage team members.</p></Card>
          )}
        </div>
      ) : null}

      {activeTab === "global-settings" ? (
        <div className="tabPanel active">
          {isAdmin ? <GlobalSettingsPanel /> : (
            <Card title="Global settings"><p className="muted">Only administrators can change global settings.</p></Card>
          )}
        </div>
      ) : null}

      {activeTab === "settings-skeletons" ? (
        <div className="tabPanel active">
          {isAdmin ? <SettingsSkeletonsPanel /> : (
            <Card title="Settings presets"><p className="muted">Only administrators can manage settings presets.</p></Card>
          )}
        </div>
      ) : null}

      {activeTab === "plans" ? (
        <div className="tabPanel active">
          {isAdmin ? (
            <PlansPanel sites={allTenants} onSitePlanChange={loadContext} />
          ) : (
            <Card title="Plans"><p className="muted">Only administrators can manage plans.</p></Card>
          )}
        </div>
      ) : null}

      {activeTab === "credit-rates" ? (
        <div className="tabPanel active">
          {isAdmin ? (
            <Card title="Credit rates">
              <p className="muted">Fleet-wide credit rates for monthly usage billing.</p>
              <div className="billingGrid">
                {BILLING_IDS.map((id) => {
                  const c = billingRates?.categories?.[id] || {};
                  return (
                    <div key={id} className="billingRateCard">
                      <h4>{c.label || id}</h4>
                      <p>{c.description || ""}</p>
                      <label className="muted" style={{ fontSize: 10 }}>Credits per {c.unit || "unit"}</label>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        className="mono"
                        style={{ marginTop: 4, width: "100%" }}
                        value={billingDraft[id] ?? 0}
                        onChange={(e) => setBillingDraft((prev) => ({ ...prev, [id]: Number(e.target.value) }))}
                      />
                    </div>
                  );
                })}
              </div>
              <Toolbar>
                <span className="muted mono" style={{ fontSize: 11 }}>{billingMsg}</span>
                <button type="button" className="btn primary" onClick={() => void saveBillingRates()}>Save credit rates</button>
              </Toolbar>
            </Card>
          ) : (
            <Card title="Credit rates"><p className="muted">Only administrators can change credit rates.</p></Card>
          )}
        </div>
      ) : null}

      <UserModal
        open={userModal.open}
        edit={userModal.edit}
        tenants={allTenants}
        onClose={() => setUserModal({ open: false })}
        onSave={saveUser}
      />

      <AddSiteWizard
        key={addSiteResumeDomain ? `resume-${addSiteResumeDomain}-${addSiteResumeStep}` : "new-site"}
        open={addSiteOpen}
        initialDomain={addSiteResumeDomain}
        initialStep={addSiteResumeStep}
        onRegistered={() => loadContext()}
        onClose={() => {
          setAddSiteOpen(false);
          setAddSiteResumeDomain("");
          setAddSiteResumeStep(0);
        }}
        onComplete={(domain) => {
          setAddSiteOpen(false);
          setAddSiteResumeDomain("");
          setAddSiteResumeStep(0);
          void loadContext().then(() => {
            location.href = dashboardHref(domain);
          });
        }}
      />

      <DeleteSiteModal
        open={deleteModal.open}
        domain={deleteModal.domain}
        busy={deleteBusy}
        error={deleteError}
        confirmText={deleteConfirm}
        onConfirmTextChange={setDeleteConfirm}
        onClose={closeDeleteSite}
        onConfirm={() => void submitDeleteSite()}
      />

    </div>
  );
}
